import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./SessionProvider.module.css";
import { subscribeBackendRequestActivity } from "../../utils/backendActivity";

const parsePositiveNumber = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return fallback;
};

const INACTIVITY_LIMIT_MS =
  parsePositiveNumber(process.env.REACT_APP_SESSION_INACTIVITY_MINUTES, 9) * 60 * 1000;
const WARNING_DURATION_MS =
  parsePositiveNumber(process.env.REACT_APP_SESSION_WARNING_SECONDS, 60) * 1000;

const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const SessionProvider = ({ children, enabled, onLogout, onStayAliveRequest }) => {
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(WARNING_DURATION_MS);
  const [isStayAliveLoading, setIsStayAliveLoading] = useState(false);

  const inactivityTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityAtRef = useRef(0);

  const clearInactivityTimeout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const clearWarningTimers = useCallback(() => {
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const openWarning = useCallback(() => {
    clearWarningTimers();
    const warningEndsAt = Date.now() + WARNING_DURATION_MS;
    setIsWarningOpen(true);
    setRemainingMs(WARNING_DURATION_MS);

    countdownIntervalRef.current = setInterval(() => {
      setRemainingMs(Math.max(0, warningEndsAt - Date.now()));
    }, 250);

    logoutTimeoutRef.current = setTimeout(() => {
      clearWarningTimers();
      setIsWarningOpen(false);
      setRemainingMs(WARNING_DURATION_MS);
      onLogout();
    }, WARNING_DURATION_MS);
  }, [clearWarningTimers, onLogout]);

  const resetSessionTimers = useCallback(
    (force = false) => {
      if (!enabled) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastActivityAtRef.current < 300) {
        return;
      }

      lastActivityAtRef.current = now;
      clearInactivityTimeout();
      clearWarningTimers();
      setIsWarningOpen(false);
      setRemainingMs(WARNING_DURATION_MS);

      inactivityTimeoutRef.current = setTimeout(() => {
        openWarning();
      }, INACTIVITY_LIMIT_MS);
    },
    [enabled, clearInactivityTimeout, clearWarningTimers, openWarning]
  );

  useEffect(() => {
    if (!enabled) {
      clearInactivityTimeout();
      clearWarningTimers();
      setIsWarningOpen(false);
      setRemainingMs(WARNING_DURATION_MS);
      return undefined;
    }

    resetSessionTimers(true);

    const unsubscribe = subscribeBackendRequestActivity(() => resetSessionTimers(true));

    return () => {
      unsubscribe();
      clearInactivityTimeout();
      clearWarningTimers();
    };
  }, [enabled, resetSessionTimers, clearInactivityTimeout, clearWarningTimers]);

  const progress = useMemo(
    () => Math.min(100, (remainingMs / WARNING_DURATION_MS) * 100),
    [remainingMs]
  );

  const onClickStayAlive = useCallback(async () => {
    if (isStayAliveLoading || typeof onStayAliveRequest !== "function") {
      return;
    }

    setIsStayAliveLoading(true);
    try {
      await onStayAliveRequest();
    } finally {
      setIsStayAliveLoading(false);
    }
  }, [isStayAliveLoading, onStayAliveRequest]);

  return (
    <>
      {children}
      {enabled && isWarningOpen && (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-live="assertive">
          <div className={styles.dialog}>
            <div className={styles.badge}>Сессия безопасности</div>
            <h2 className={styles.title}>Вы давно не проявляли активность</h2>
            <p className={styles.description}>
              Если в течение времени ниже не будет действий на сайте, система автоматически завершит
              сеанс и выполнит выход из аккаунта.
            </p>
            <div className={styles.timerWrap}>
              <div className={styles.timerRing} style={{ "--session-progress": `${progress}%` }}>
                <span>{formatCountdown(remainingMs)}</span>
              </div>
            </div>
            <button type="button" className={styles.button} onClick={onClickStayAlive}>
              {isStayAliveLoading ? "Проверяем сессию..." : "Я на месте, продолжить работу"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionProvider;
